import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';

const Page = styled.div`
  max-width: 1100px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Header = styled.h1`
  color: var(--text);
  margin-bottom: 16px;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const Card = styled(Link)`
  display: block;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 10px;
  text-decoration: none;
  color: var(--text);
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
`;

const Img = styled.img`
  width: 100%;
  height: 160px;
  object-fit: contain;
  background: var(--bg);
`;

const Body = styled.div`
  padding: 12px 14px;
`;

const Name = styled.h3`
  margin: 0 0 8px;
  font-size: 1.05rem;
`;

const Price = styled.div`
  color: var(--accent);
  font-weight: 700;
`;

const Products = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const gridRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/products');
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to load products');
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load products');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useLayoutEffect(() => {
    if (!items?.length || !gridRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(gridRef.current.children, { y: 16, autoAlpha: 0, duration: 1, ease: 'power2.out', stagger: 0.06 });
    }, gridRef);
    return () => ctx.revert();
  }, [items]);

  return (
    <Page>
      <Header>Products</Header>
      {loading && <div style={{ color: 'var(--muted)', marginBottom: 12 }}>Loading…</div>}
      {error && <div style={{ color: '#b91c1c', marginBottom: 12 }}>{error}</div>}
      <Grid ref={gridRef}>
        {items.map((p) => {
          const priceText = typeof p.price === 'number' ? `$${p.price.toFixed(2)}` : '';
          return (
            <Card key={p.id} to={`/products/${p.slug}`}>
              <Img src={p.image} alt={p.name} />
              <Body>
                <Name>{p.name}</Name>
                <Price>{priceText}</Price>
              </Body>
            </Card>
          );
        })}
      </Grid>
      {!loading && !error && items.length === 0 ? (
        <div style={{ color: 'var(--muted)', marginTop: 12 }}>No products available.</div>
      ) : null}
    </Page>
  );
};

export default Products;
